import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Form, FormInstance } from 'rsuite';
import {
  PresentationGrid,
  PresentationRow,
  PresentationCol,
} from '../../layout/PresentationLayout';
import FormTextField from '../FormTextField';
import { FORM_INPUT_TYPES } from '../../../common/enums';
import {
  convertSnakeCaseToCamelCase,
  objectHasProperty,
} from '../../../common/utils';
import { AddressField } from './AddressField';
import { Schema } from 'schema-typed';

import { lookupAddress } from '../../../api/apiAccess';
import { FormModelPerson } from 'src/types/person';
import { AddressOption, LookupAddressResponse } from 'src/types/address';
import { parseSmartyResultToAddress } from '../AddressLookup';

type HiddenFieldsRecord = Record<keyof FormModelPerson, boolean>;
export interface PersonDetailsProps {
  disabled?: boolean;
  disabledFields?: Partial<Record<keyof FormModelPerson, boolean>>;
  formModel?: Schema<FormModelPerson, string>;
  formValues: FormModelPerson;
  hiddenFields?: Partial<HiddenFieldsRecord>;
  optionalFields?: Record<string, 'optional' | 'required'>;
  propertyAddress?: string;
  restrictAddressToCity?: boolean;
  setFormValues: (value: FormModelPerson) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationModel: Record<string, any>;
}

const PersonDetails = forwardRef<FormInstance, PersonDetailsProps>(
  (
    {
      disabled = false,
      disabledFields = {},
      formModel,
      formValues,
      hiddenFields = {},
      optionalFields = {},
      propertyAddress,
      restrictAddressToCity = false,
      setFormValues,
      validationModel,
    },
    ref
  ) => {
    const [formError, setFormError] = useState<Record<string, string>>({});
    const [fullPropertyAddress, setFullPropertyAddress] =
      useState<AddressOption>();

    const formValuesRef = useRef(formValues);
    formValuesRef.current = formValues;

    useEffect(() => {
      (async () => {
        if (!propertyAddress) return undefined;
        const { data } = (await lookupAddress(propertyAddress)) as {
          data: LookupAddressResponse;
        };
        if (data?.suggestions?.[0]) {
          const fullAddress = parseSmartyResultToAddress(data.suggestions[0]);
          setFullPropertyAddress(fullAddress);
        }
      })();
    }, [propertyAddress]);

    const parsedOptionalFields = useMemo((): Record<string, string> => {
      if (!optionalFields) {
        return {};
      }
      return Object.keys(optionalFields).reduce(
        (prev: Record<string, string>, value: string) => {
          const key = convertSnakeCaseToCamelCase(value);
          prev[key] = optionalFields[value] as string;
          return prev;
        },
        {}
      );
    }, [optionalFields]);

    const globalDisabled = useMemo(() => {
      const hasSingleDisabledFields =
        Object.values(disabledFields).filter((value) => value).length > 0;
      return !hasSingleDisabledFields && disabled;
    }, [disabled, disabledFields]);

    const resolveMailingAddressComponents = useCallback(
      async (values: FormModelPerson) => {
        if (values?.mailingAddressComponents) {
          return values.mailingAddressComponents;
        }
        if (!values?.mailingAddress) {
          return undefined;
        }
        const { data } = (await lookupAddress(values.mailingAddress)) as {
          data: LookupAddressResponse;
        };
        const first = data?.suggestions?.[0];
        return first ? parseSmartyResultToAddress(first).components : undefined;
      },
      []
    );

    const isFieldOptional = useCallback(
      (fieldName: string): boolean =>
        objectHasProperty(fieldName, parsedOptionalFields) &&
        parsedOptionalFields[fieldName] === 'optional',
      [parsedOptionalFields]
    );

    const formChange = useCallback(
      async (values: Partial<FormModelPerson>) => {
        // rsuite's Form.onChange fires with the full formValue spread whenever any
        // Form.Control changes, including the checkbox proxy controls named
        // "checkboxsameas-*". Those checkbox changes are already handled by
        // onSameAsPropertyAddressChanged / handleSameAsMailingAddressChange, so we
        // must not process them here — both setFormValues calls land in the same React
        // batch and rsuite's (last) call would overwrite the correct value with stale
        // formValues from the closure.
        if (Object.keys(values).some((k) => k.startsWith('checkboxsameas-'))) {
          return;
        }

        const normalized = { ...values };

        const mailingAddressChanged =
          'mailingAddress' in normalized &&
          normalized.mailingAddress !== formValuesRef.current.mailingAddress;

        if (mailingAddressChanged) {
          if (typeof normalized.mailingAddress !== 'string') {
            const addressOption =
              normalized.mailingAddress as unknown as AddressOption;
            normalized.mailingAddress = addressOption?.value ?? '';
            normalized.mailingAddressComponents = addressOption?.components;
          } else {
            normalized.mailingAddressComponents = undefined;
          }
        }
        const next = { ...formValuesRef.current, ...normalized };
        if (
          mailingAddressChanged &&
          next.isPhysicalAddressSameAsMailingAddress
        ) {
          const components = await resolveMailingAddressComponents(next);
          next.completePhysicalAddress = {
            label: next.mailingAddress,
            value: next.mailingAddress,
            components,
          };
        }
        setFormValues(next);
      },
      [resolveMailingAddressComponents, setFormValues]
    );

    const onSameAsPropertyAddressChanged = useCallback(
      (checked: boolean): void => {
        formChange({
          sameAsPropertyAddress: checked,
          mailingAddress: fullPropertyAddress?.value || '',
          ...(fullPropertyAddress?.components
            ? { mailingAddressComponents: fullPropertyAddress.components }
            : {}),
        });
      },
      [formChange, fullPropertyAddress]
    );

    const handleSameAsMailingAddressChange = useCallback(
      async (checked: boolean): Promise<void> => {
        const { completePhysicalAddress: _ignored, ...restErrors } = formError;
        void _ignored;
        setFormError(restErrors);

        if (!checked) {
          formChange({
            completePhysicalAddress:
              formValuesRef.current.completePhysicalAddress,
            isPhysicalAddressSameAsMailingAddress: false,
          });
          return;
        }

        const components = await resolveMailingAddressComponents(
          formValuesRef.current
        );

        formChange({
          completePhysicalAddress: {
            components,
            label: formValuesRef.current?.mailingAddress,
            value: formValuesRef.current?.mailingAddress,
          },
          isPhysicalAddressSameAsMailingAddress: true,
        });
      },
      [formChange, formError, resolveMailingAddressComponents]
    );

    return (
      <Form
        model={formModel}
        ref={ref}
        checkTrigger="none"
        formValue={formValues}
        onCheck={setFormError}
        onChange={formChange}
        fluid
      >
        <p className="text-small mb-12">* Required</p>
        <PresentationGrid fluid>
          {(!hiddenFields.firstName || !hiddenFields.lastName) && (
            <PresentationRow
              gutter={24}
              className="mb-12"
            >
              {!hiddenFields.firstName && (
                <PresentationCol
                  xs={24}
                  md={12}
                  className="sm-pb-12"
                >
                  <FormTextField
                    data-cy="person-first-name"
                    name="firstName"
                    label={`First Name${isFieldOptional('firstName') ? ' (optional)' : ''}`}
                    required={!isFieldOptional('firstName')}
                    hasError={!!formError['firstName']}
                    validationRule={validationModel.firstName}
                    errorMessage={formError['firstName']}
                    disabled={disabledFields.firstName || globalDisabled}
                    value={formValues?.firstName ?? ''}
                    autoComplete="given-name"
                  />
                </PresentationCol>
              )}
              {!hiddenFields.lastName && (
                <PresentationCol
                  xs={24}
                  md={12}
                >
                  <FormTextField
                    data-cy="person-last-name"
                    name="lastName"
                    label={`Last Name${isFieldOptional('lastName') ? ' (optional)' : ''}`}
                    required={!isFieldOptional('lastName')}
                    hasError={!!formError['lastName']}
                    validationRule={validationModel.lastName}
                    errorMessage={formError['lastName']}
                    disabled={disabledFields.lastName || globalDisabled}
                    value={formValues?.lastName ?? ''}
                    autoComplete="family-name"
                  />
                </PresentationCol>
              )}
            </PresentationRow>
          )}
          {!hiddenFields.companyName && (
            <div className="mb-12">
              <FormTextField
                data-cy="person-company"
                name="companyName"
                label={`Company Name${isFieldOptional('companyName') ? ' (optional)' : ''}`}
                hasError={!!formError['companyName']}
                validationRule={validationModel.companyName}
                value={formValues?.companyName ?? ''}
                errorMessage={formError['companyName']}
                disabled={disabledFields.companyName || globalDisabled}
                autoComplete="organization"
              />
            </div>
          )}
          {!hiddenFields.email && (
            <div className="mb-12">
              <FormTextField
                data-cy="person-email"
                name="email"
                label={`Email Address${isFieldOptional('email') ? ' (optional)' : ''}`}
                required={!isFieldOptional('email')}
                hasError={!!formError['email']}
                validationRule={validationModel.email}
                errorMessage={formError['email']}
                disabled={disabledFields.email || globalDisabled}
                value={formValues?.email ?? ''}
                type={FORM_INPUT_TYPES.EMAIL}
                autoComplete="email"
              />
            </div>
          )}
          {!hiddenFields.phone && (
            <div className="mb-12">
              <FormTextField
                autoComplete="tel"
                data-cy="person-phone-number"
                name="phone"
                label={`Phone Number${isFieldOptional('phone') ? ' (optional)' : ''}`}
                required={!isFieldOptional('phone')}
                hasError={!!formError['phone']}
                validationRule={validationModel.phone}
                errorMessage={formError['phone']}
                type={FORM_INPUT_TYPES.PHONE}
                disabled={disabledFields.phone || globalDisabled}
                value={formValues?.phone ?? ''}
              />
            </div>
          )}
          {!hiddenFields.mailingAddress && (
            <AddressField
              autoComplete="street-address"
              checkboxLabel="Same as property address"
              disabled={disabledFields.mailingAddress || globalDisabled}
              errorMessage={formError['mailingAddress']}
              isChecked={!!formValues?.sameAsPropertyAddress}
              label={`Mailing Address${isFieldOptional('mailingAddress') ? ' (optional)' : ' *'}`}
              name="mailingAddress"
              onCheckboxChange={onSameAsPropertyAddressChanged}
              restrictAddressToCity={restrictAddressToCity}
              showCheckbox={!hiddenFields.sameAsPropertyAddress}
              validationRule={validationModel.mailingAddress}
              value={formValues?.mailingAddress ?? ''}
            />
          )}
          {!hiddenFields.physicalAddress && (
            <AddressField
              autoComplete="street-address"
              checkboxLabel="Same as mailing address"
              checkTrigger="none"
              disabled={disabledFields.physicalAddress || globalDisabled}
              errorMessage={formError['completePhysicalAddress']}
              isChecked={!!formValues?.isPhysicalAddressSameAsMailingAddress}
              label={`Physical Address${isFieldOptional('completePhysicalAddress') ? ' (optional)' : ' *'}`}
              name="completePhysicalAddress"
              onCheckboxChange={handleSameAsMailingAddressChange}
              showCheckbox={!!formValues?.mailingAddress}
              validationRule={validationModel.completePhysicalAddress}
              value={formValues?.completePhysicalAddress ?? ''}
            />
          )}
        </PresentationGrid>
      </Form>
    );
  }
);

PersonDetails.displayName = 'PersonDetails';
export default PersonDetails;
